import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Divider, Flex, Form, Input, InputNumber, Switch } from 'antd';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import useApiClient from '../../hooks/api/useApiClient';
import { BusinessProfileUpdateStrategy } from '../../providers/BusinessProfileProvider';

export default function TaxInfoStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:TaxInfoStep' });

  const { buttonText, isSkippable, moveBackwards, moveForward, skip, skipping } = props;

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { getValue, companies, updating, pushUpdates } = useBusinessProfile();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });
  const { CompaniesManager } = useApiClient();

  const { data, update } = useForm({
    tax_id: getValue('tax_id'),
    vat_id: getValue('vat_id'),
    has_vat: Boolean(getValue('vat_id')),
    tax_percentage: company.tax_percentage ?? serviceProvider?.regional_config?.tax_percentage ?? null,
    has_dedicated_products_tax: company.has_dedicated_products_tax ?? false,
  });

  // The two tax fields live on the company and are no longer accepted by
  // PUT /business-profile: the VAT rate moved out of the payment flow so a
  // restaurant that never takes a card still gets one (WLP Payments 2/8). They are
  // written only once the profile went through, to the outlets the partner picked
  // for it in the update-strategy modal — saving first would change the current
  // outlet even when the modal is then dismissed.
  const saveCompanyTax = async (strategy) => {
    const payload = {
      // Omitted rather than null when blank — the rule is `sometimes|numeric`, so a
      // present null is a 422.
      ...(data.tax_percentage === null || data.tax_percentage === '' ? {} : { tax_percentage: data.tax_percentage }),
      has_dedicated_products_tax: data.has_dedicated_products_tax,
    };
    const otherOutlets =
      strategy === BusinessProfileUpdateStrategy.All ? companies.filter((item) => item.id !== company.id) : [];

    const responses = await Promise.all([
      CompaniesManager.updateAndSetCurrentCompany(company.id, payload),
      ...otherOutlets.map((item) => CompaniesManager.update(item.id, payload)),
    ]);

    return responses.find((response) => !response?.success) ?? responses[0];
  };

  const save = () =>
    pushUpdates({
      data: {
        tax_id: data.tax_id,
        vat_id: data.has_vat ? data.vat_id : null,
      },
      onSuccess: async (_response, strategy) => {
        const taxResponse = await saveCompanyTax(strategy);

        if (!taxResponse?.success) {
          setErrorFeedback(taxResponse?.data);

          return;
        }

        // Only the onboarding stepper passes `skip`: there an empty tax number marks the
        // step skipped server-side. The standalone Tax Details page just saves.
        if (skip && !data?.tax_id && !data?.vat_id) {
          return skip();
        }

        return moveForward?.();
      },
      onError: ({ data }) => setErrorFeedback(data),
    });

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <Form layout="vertical">
        <Form.Item label={t('form.tax_id.label')}>
          <Input
            placeholder={t('form.tax_id.placeholder')}
            value={data.tax_id ?? ''}
            onChange={(e) => update('tax_id', e.target.value)}
          />
        </Form.Item>
        <Form.Item>
          <Flex>
            <Switch checked={data.has_vat} onChange={(checked) => update('has_vat', checked)} id="has_vat" />
            <label htmlFor="has_vat" className="ml-xs my-auto">
              {t('form.has_vat.label')}
            </label>
          </Flex>
        </Form.Item>
        {data.has_vat && (
          <Form.Item label={t('form.vat_id.label')} tooltip={t('form.vat_id.tooltip')} required>
            <Input
              placeholder={t('form.vat_id.placeholder')}
              value={data.vat_id ?? ''}
              onChange={(e) => update('vat_id', e.target.value)}
            />
          </Form.Item>
        )}
        <Divider />
        {!serviceProvider?.is_master && (
          <Form.Item label={t('form.tax_percentage.label')} required>
            <InputNumber
              placeholder={t('form.tax_percentage.placeholder')}
              value={data.tax_percentage ?? ''}
              onChange={(value) => update('tax_percentage', value)}
              min={0}
              max={100}
            />
          </Form.Item>
        )}
        <Form.Item>
          <Flex>
            <Switch
              checked={data.has_dedicated_products_tax}
              onChange={(checked) => update('has_dedicated_products_tax', checked)}
              id="has_dedicated_products_tax"
            />
            <label htmlFor="has_dedicated_products_tax" className="ml-xs my-auto">
              {t('form.has_dedicated_products_tax.label')}
            </label>
          </Flex>
        </Form.Item>
      </Form>
      <Flex justify="space-between">
        {isSkippable ? (
          <Button color="default" variant="solid" onClick={skip} loading={skipping}>
            {t('buttons.skip')}
          </Button>
        ) : (
          <span />
        )}
        <Flex justify="end">
          {moveBackwards && (
            <Button onClick={moveBackwards} className="mr-md">
              {t('buttons.back')}
            </Button>
          )}
          <Button type="primary" onClick={save} loading={updating}>
            {buttonText ?? t('buttons.next')}
          </Button>
        </Flex>
      </Flex>
    </FadeIn>
  );
}
